import { ExternalLink, Copy, Check } from 'lucide-react';
import { useState, Fragment } from 'react';
import toast from 'react-hot-toast';
// Same content as https://rupixpe.com/api-docs (api-docs/build-docs.mjs)
import { SECTIONS, BASE_URL, UPDATED } from '../../../api-docs/docs-content.mjs';

// `code` and **bold** inside plain text
const Inline = ({ text }) => {
  const parts = String(text).split(/(`[^`]+`|\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('`') && part.endsWith('`')) {
      return (
        <code key={i} className="bg-gray-100 text-purple-700 px-1 py-0.5 rounded text-[0.85em] font-mono">
          {part.slice(1, -1)}
        </code>
      );
    }
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <Fragment key={i}>{part}</Fragment>;
  });
};

const NOTE_CLASSES = {
  info: 'bg-blue-50 border-blue-500 text-blue-900',
  warn: 'bg-yellow-50 border-yellow-500 text-yellow-900',
  danger: 'bg-red-50 border-red-500 text-red-900'
};

const METHOD_CLASSES = {
  GET: 'bg-blue-500',
  POST: 'bg-green-500'
};

const ApiDocs = () => {
  const [copiedId, setCopiedId] = useState(null);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedId(null), 2000);
  };

  const CodeBlock = ({ code, id }) => (
    <div className="relative bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto">
      <button
        onClick={() => copyToClipboard(code, id)}
        className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded transition-colors"
      >
        {copiedId === id ? <Check size={16} /> : <Copy size={16} />}
      </button>
      <pre className="text-sm font-mono"><code>{code}</code></pre>
    </div>
  );

  const renderBlock = (block, key) => {
    if (block.p) return <p key={key} className="text-gray-700"><Inline text={block.p} /></p>;
    if (block.h3) return <h3 key={key} className="font-semibold text-lg text-gray-900 pt-4"><Inline text={block.h3} /></h3>;
    if (block.h4) return <h4 key={key} className="font-medium text-gray-800 pt-2"><Inline text={block.h4} /></h4>;
    if (block.list) {
      return (
        <ul key={key} className="list-disc pl-6 space-y-1 text-gray-700">
          {block.list.map((item, i) => <li key={i}><Inline text={item} /></li>)}
        </ul>
      );
    }
    if (block.note) {
      return (
        <div key={key} className={`border-l-4 p-4 rounded text-sm ${NOTE_CLASSES[block.note.kind]}`}>
          <Inline text={block.note.text} />
        </div>
      );
    }
    if (block.endpoint) {
      const { method, path, desc } = block.endpoint;
      return (
        <div key={key} className="bg-gray-50 p-3 rounded border-l-4 border-primary-500">
          <span className={`inline-block text-white px-3 py-1 rounded text-sm font-semibold mr-2 ${METHOD_CLASSES[method]}`}>
            {method}
          </span>
          <code className="text-sm font-semibold">{path}</code>
          {desc && <p className="text-sm text-gray-600 mt-2"><Inline text={desc} /></p>}
        </div>
      );
    }
    if (block.table) {
      return (
        <div key={key} className="overflow-x-auto">
          <table className="min-w-full text-sm border border-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {block.table.head.map((cell, i) => (
                  <th key={i} className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-200">
                    <Inline text={cell} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {block.table.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} className="px-3 py-2 align-top text-gray-700"><Inline text={cell} /></td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    if (block.code) return <CodeBlock key={key} code={block.code.text} id={key} />;
    return null;
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">API Documentation</h1>
          <p className="text-sm text-gray-500 mt-1">
            Base URL: <code className="font-mono">{BASE_URL}</code> &middot; Updated {UPDATED}
          </p>
        </div>
        <a
          href="https://rupixpe.com/api-docs"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <ExternalLink size={16} className="mr-2" />
          Open Public Docs
        </a>
      </div>

      {/* Section navigation */}
      <div className="card p-3 flex flex-wrap gap-2">
        {SECTIONS.map(section => (
          <button
            key={section.id}
            onClick={() => document.getElementById(`docs-${section.id}`)?.scrollIntoView({ behavior: 'smooth' })}
            className="px-3 py-1 text-sm font-medium rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50"
          >
            {section.title}
          </button>
        ))}
      </div>

      {SECTIONS.map(section => (
        <div key={section.id} id={`docs-${section.id}`} className="card p-6 space-y-4 scroll-mt-4">
          <h2 className="text-xl font-semibold text-gray-900">{section.title}</h2>
          {section.blocks.map((block, i) => renderBlock(block, `${section.id}-${i}`))}
        </div>
      ))}
    </div>
  );
};

export default ApiDocs;
